<script setup>
import {
  onBeforeUnmount,
  ref,
} from 'vue';

import ContactAPI from 'dashboard/api/contacts';
import TicketsAPI from 'dashboard/api/tickets';

import { useAlert } from 'dashboard/composables';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  statuses: {
    type: Array,
    default: () => [],
  },
  categories: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits([
  'close',
  'created',
]);

const contactQuery = ref('');
const contacts = ref([]);
const selectedContact = ref(null);

const subject = ref('');
const content = ref('');

const statusId = ref('');
const categoryId = ref('');
const priority = ref('');

const isSearchingContacts = ref(false);
const isCreating = ref(false);

let searchTimer = null;
let searchController = null;

const resetSearchTimer = () => {
  if (searchTimer) {
    clearTimeout(searchTimer);
    searchTimer = null;
  }

  if (searchController) {
    searchController.abort();
    searchController = null;
  }
};

const searchContacts = async () => {
  const query = contactQuery.value.trim();

  if (query.length < 2) {
    contacts.value = [];
    return;
  }

  resetSearchTimer();

  searchTimer = setTimeout(async () => {
    isSearchingContacts.value = true;
    searchController = new AbortController();

    try {
      const response = await ContactAPI.search(
        query,
        1,
        'name',
        '',
        {
          signal: searchController.signal,
        }
      );

      contacts.value = response.data?.payload || [];
    } catch (error) {
      if (error?.name !== 'CanceledError') {
        contacts.value = [];
      }
    } finally {
      isSearchingContacts.value = false;
      searchController = null;
    }
  }, 300);
};

const selectContact = contact => {
  selectedContact.value = contact;
  contactQuery.value =
    contact.name ||
    contact.email ||
    contact.phone_number ||
    `Contato #${contact.id}`;

  contacts.value = [];
};

const clearContact = () => {
  selectedContact.value = null;
  contactQuery.value = '';
  contacts.value = [];
};

const createTicket = async () => {
  if (!selectedContact.value?.id) {
    useAlert('Selecione um solicitante.');
    return;
  }

  if (!subject.value.trim()) {
    useAlert('Informe o assunto do ticket.');
    return;
  }

  if (!content.value.trim()) {
    useAlert('Informe a descrição inicial do ticket.');
    return;
  }

  isCreating.value = true;

  try {
    const ticket = {
      contact_id: selectedContact.value.id,
      subject: subject.value.trim(),
      content: content.value.trim(),
    };

    if (statusId.value) {
      ticket.status_id = Number(statusId.value);
    }

    if (categoryId.value) {
      ticket.category_id = Number(categoryId.value);
    }

    if (priority.value) {
      ticket.priority = priority.value;
    }

    const response = await TicketsAPI.create({
      ticket,
    });

    useAlert('Ticket criado com sucesso.');

    emit('created', response.data);
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        'Não foi possível criar o ticket.'
    );
  } finally {
    isCreating.value = false;
  }
};

onBeforeUnmount(() => {
  resetSearchTimer();
});
</script>

<template>
  <div
    class="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
    @click.self="emit('close')"
  >
    <div
      class="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1 shadow-xl"
    >
      <header
        class="flex items-center justify-between gap-4 border-b border-n-weak px-5 py-4"
      >
        <div>
          <h2 class="text-base font-semibold text-n-slate-12">
            Novo ticket
          </h2>

          <p class="mt-0.5 text-xs text-n-slate-10">
            Crie um atendimento diretamente na Central de Tickets.
          </p>
        </div>

        <button
          type="button"
          class="inline-flex size-9 items-center justify-center rounded-lg hover:bg-n-alpha-2"
          @click="emit('close')"
        >
          <span class="i-lucide-x size-5" />
        </button>
      </header>

      <div class="flex-1 overflow-y-auto p-5">
        <div class="flex flex-col gap-5">
          <div class="relative">
            <label class="mb-1.5 block text-sm font-medium text-n-slate-11">
              Solicitante
            </label>

            <div
              v-if="selectedContact"
              class="flex min-h-11 items-center justify-between gap-3 rounded-xl border border-n-weak bg-n-alpha-1 px-3"
            >
              <div class="min-w-0">
                <p class="truncate text-sm font-medium text-n-slate-12">
                  {{
                    selectedContact.name ||
                    selectedContact.email ||
                    selectedContact.phone_number
                  }}
                </p>

                <p
                  v-if="
                    selectedContact.email ||
                    selectedContact.phone_number
                  "
                  class="truncate text-xs text-n-slate-9"
                >
                  {{
                    selectedContact.email ||
                    selectedContact.phone_number
                  }}
                </p>
              </div>

              <button
                type="button"
                class="text-xs font-medium text-n-brand"
                @click="clearContact"
              >
                Trocar
              </button>
            </div>

            <template v-else>
              <div class="relative">
                <span
                  class="i-lucide-search absolute left-3 top-1/2 size-4 -translate-y-1/2 text-n-slate-8"
                />

                <input
                  v-model="contactQuery"
                  type="search"
                  autocomplete="off"
                  placeholder="Nome, e-mail ou telefone"
                  class="h-11 w-full rounded-xl border border-n-weak bg-n-alpha-1 pl-9 pr-10 text-sm text-n-slate-12 outline-none focus:border-n-brand"
                  @input="searchContacts"
                />

                <Spinner
                  v-if="isSearchingContacts"
                  class="absolute right-3 top-1/2 -translate-y-1/2 text-n-brand"
                />
              </div>

              <div
                v-if="contacts.length"
                class="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-xl border border-n-weak bg-n-solid-1 p-1 shadow-lg"
              >
                <button
                  v-for="contact in contacts"
                  :key="contact.id"
                  type="button"
                  class="flex w-full flex-col rounded-lg px-3 py-2.5 text-left hover:bg-n-alpha-2"
                  @click="selectContact(contact)"
                >
                  <span class="text-sm font-medium text-n-slate-12">
                    {{
                      contact.name ||
                      contact.email ||
                      contact.phone_number ||
                      `Contato #${contact.id}`
                    }}
                  </span>

                  <span
                    v-if="contact.email || contact.phone_number"
                    class="mt-0.5 text-xs text-n-slate-9"
                  >
                    {{ contact.email || contact.phone_number }}
                  </span>
                </button>
              </div>
            </template>
          </div>

          <div>
            <label class="mb-1.5 block text-sm font-medium text-n-slate-11">
              Assunto
            </label>

            <input
              v-model="subject"
              type="text"
              maxlength="255"
              placeholder="Ex.: Instabilidade no acesso"
              class="h-11 w-full rounded-xl border border-n-weak bg-n-alpha-1 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
            />
          </div>

          <div>
            <label class="mb-1.5 block text-sm font-medium text-n-slate-11">
              Descrição
            </label>

            <textarea
              v-model="content"
              rows="6"
              placeholder="Descreva a solicitação inicial..."
              class="w-full resize-y rounded-xl border border-n-weak bg-n-alpha-1 px-3 py-2.5 text-sm text-n-slate-12 outline-none focus:border-n-brand"
            />
          </div>

          <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label class="mb-1.5 block text-xs font-medium text-n-slate-9">
                Status
              </label>

              <select
                v-model="statusId"
                class="h-10 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-2 text-sm text-n-slate-12"
              >
                <option value="">
                  Padrão
                </option>

                <option
                  v-for="status in props.statuses"
                  :key="status.id"
                  :value="status.id"
                >
                  {{ status.name }}
                </option>
              </select>
            </div>

            <div>
              <label class="mb-1.5 block text-xs font-medium text-n-slate-9">
                Categoria
              </label>

              <select
                v-model="categoryId"
                class="h-10 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-2 text-sm text-n-slate-12"
              >
                <option value="">
                  Sem categoria
                </option>

                <option
                  v-for="category in props.categories"
                  :key="category.id"
                  :value="category.id"
                >
                  {{ category.name }}
                </option>
              </select>
            </div>

            <div>
              <label class="mb-1.5 block text-xs font-medium text-n-slate-9">
                Prioridade
              </label>

              <select
                v-model="priority"
                class="h-10 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-2 text-sm text-n-slate-12"
              >
                <option value="">
                  Sem prioridade
                </option>
                <option value="low">
                  Baixa
                </option>
                <option value="medium">
                  Média
                </option>
                <option value="high">
                  Alta
                </option>
                <option value="urgent">
                  Urgente
                </option>
              </select>
            </div>
          </div>

          <div
            class="rounded-xl border border-n-weak bg-n-alpha-1 px-3 py-3 text-xs text-n-slate-10"
          >
            O agente e a equipe poderão ser atribuídos logo após a criação,
            diretamente no painel do ticket.
          </div>
        </div>
      </div>

      <footer
        class="flex items-center justify-end gap-2 border-t border-n-weak px-5 py-4"
      >
        <button
          type="button"
          class="h-9 rounded-lg px-4 text-sm font-medium text-n-slate-11 hover:bg-n-alpha-2"
          :disabled="isCreating"
          @click="emit('close')"
        >
          Cancelar
        </button>

        <button
          type="button"
          class="inline-flex h-9 items-center gap-2 rounded-lg bg-n-brand px-4 text-sm font-medium text-white disabled:opacity-50"
          :disabled="isCreating"
          @click="createTicket"
        >
          <Spinner v-if="isCreating" class="text-white" />
          <span v-else class="i-lucide-ticket-plus size-4" />

          Criar ticket
        </button>
      </footer>
    </div>
  </div>
</template>
